import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '../../shared/api/client';
import { FieldLabel } from '../../shared/ui/field-label';
import { Hint } from '../../shared/ui/hint';
import { Input } from '../../shared/ui/input';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { createOrganization } from '../../entities/organization';
import { switchOrganization } from '../../entities/session';

interface IOrganizationCreateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: () => void;
}

export const OrganizationCreate = ({ open, onOpenChange, onCreated }: IOrganizationCreateProps) => {
  const [name, setName] = useState('');
  const queryClient = useQueryClient();

  const create = useMutation({
    // Creating does not move the session; the switch is what issues tokens for
    // the new organization, and landing in the one just created is the point.
    mutationFn: async (value: string) => {
      const organization = await createOrganization(value);
      await switchOrganization(organization.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      close(false);
      onCreated?.();
    },
  });

  const close = (next: boolean) => {
    if (!next) {
      setName('');
      create.reset();
    }
    onOpenChange(next);
  };

  const trimmed = name.trim();

  return (
    <Modal
      open={open}
      onOpenChange={close}
      title="New organization"
      description="A separate workspace with its own sources, destinations and events. Your account keeps access to both."
      submit={{
        label: 'Create organization',
        onSubmit: () => create.mutate(trimmed),
        disabled: trimmed.length === 0 || create.isPending,
        waiting: create.isPending,
        waitingLabel: 'Creating',
      }}
    >
      <div className="flex flex-col gap-4 pt-2">
        <div>
          <FieldLabel htmlFor="organization-name">Name</FieldLabel>
          <Input
            id="organization-name"
            autoFocus
            value={name}
            placeholder="Acme Payments"
            onChange={(event) => setName(event.target.value)}
          />
          <Hint>
            The address your webhook URLs are built from comes from this name. You can change both
            later in settings.
          </Hint>
        </div>

        {create.error && (
          <Note tone="danger">
            {create.error instanceof ApiError
              ? create.error.message
              : 'The organization was not created.'}
          </Note>
        )}
      </div>
    </Modal>
  );
};
